// Bildschirm "Kontakt" -- die Detailansicht (ST-SEA-001, erster Schnitt).
//
// ER HAT EINE EIGENE ADRESSE, und das ist keine Kosmetik: `/kontakte/{partyId}`
// laesst sich kopieren, in einem neuen Tab oeffnen und als Lesezeichen ablegen.
// Ein Detail, das nur als Zustand einer Liste existiert, ist im Arbeitsalltag
// nicht weitergebbar -- und "schick mir mal den Kontakt" ist die haeufigste
// Bitte in einem Kontaktmanagement.
//
// GENAU EINE HAUPTAKTION (5.2 Nr. 2, NFR-UX-004): seit ST-ACT-001 (ADR-0051)
// ist es "Notiz festhalten" -- der Grund, warum jemand im Arbeitsfluss einen
// Kontakt oeffnet (CM-ACT-005: hoechstens zwei Interaktionen ab Party). Bis
// zum 01.09.2026 war es der Rueckweg zur Liste, danach die Kontaktwegpflege;
// die bleibt wie Kennungen und Organisationsnamen ein Link und keine zweite
// Betonung.
//
// PROGRESSIVE OFFENLEGUNG (NFR-UX-004): oben der Name und der Zustand, darunter
// die Kontaktwege, und erst in der aufklappbaren Sicht die Kennung und der
// Datenstand. Wer den Kontakt anrufen will, soll die Nummer sehen und keine
// UUID.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type { PlainMessage } from '../access.ts';
import { fetchParty, type PartyDetail } from '../parties.ts';
import { useKontextbefehl, useSchnellzugriff } from '../shell/kontextbefehle.ts';
import { Ladeansage, Skelett, SKELETT_KONTAKT } from '../shell/skelett.tsx';
import { Zustand } from '../shell/zustand.tsx';

import { useKontaktStyles } from './kontakt/stile.ts';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { KontaktKopf } from './kontakt/kopf.tsx';
import { KopfSkelett } from './kontakt/kopf-skelett.tsx';
import type { FollowUpTally } from '../party-360.ts';
import { FOLLOW_UP_CHOICE_ID, NOTE_INPUT_ID } from './kontakt/ids.ts';
import { angebotene, Registerleiste, type KontaktKontext } from './kontakt/register.tsx';
import type { SperrenFuer } from './kontakt/sperren-abschnitt.tsx';
import type { KontaktScreenProps } from './kontakt/typen.ts';

// Die Kennungen und Eigenschaften bleiben hier erreichbar: Prueftstand und
// Einstieg importieren sie aus diesem Bildschirm.
export * from './kontakt/ids.ts';
export type { KontaktScreenProps } from './kontakt/typen.ts';

type Status = 'laedt' | 'geladen' | 'unbekannt' | 'abgelehnt';

export function KontaktScreen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  accountId,
}: KontaktScreenProps): ReactElement {
  const own = useKontaktStyles();
  const schnellzugriff = useSchnellzugriff();
  // Erst nach einem ausdruecklichen "Erneut versuchen" geht der Fokus auf den Titel.
  const [nachErneut, setNachErneut] = useState(false);
  const [party, setParty] = useState<PartyDetail | undefined>(undefined);
  const [status, setStatus] = useState<Status>('laedt');
  // Gelingt der Abruf nach "Erneut versuchen", ist der Knopf ausgehaengt; der
  // Fokus geht auf den Hauptbereich statt auf das Dokument (WCAG 2.4.3).
  useEffect(() => {
    if (nachErneut && status === 'geladen') {
      setNachErneut(false);
      document.getElementById('hauptbereich')?.focus();
    }
  }, [nachErneut, status]);
  // Die <h1> und der Fenstertitel heissen wie der Kontakt, sobald er geladen ist.
  // Beim Wechsel A nach B bleibt `party` (A) bis der neue Abruf laeuft: gezeigt wird
  // nur ein Kontakt, der zur Adresse passt, sonst das Skelett.
  const aktuell = party !== undefined && party.partyId === partyId;
  useSeitenueberschrift(status === 'geladen' && aktuell ? party?.displayName : undefined);
  // Der Name steht in der Kopfkarte (kopf.tsx); der Rahmen haelt seine h1 bis dahin verborgen.
  useUeberschriftImInhalt();
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  /** Die offenen Wiedervorlagen, wie der Verlauf sie meldet (party-360.ts). */
  const [tally, setTally] = useState<FollowUpTally | undefined>(undefined);
  /** Die offenen Abschnitte; gefuehrt, damit ein Hinweis im Kopf einen Abschnitt oeffnen kann. */
  const [offen, setOffen] = useState<readonly string[]>(['zugehoerigkeit', 'beziehungen']);
  const [fokusAufName, setFokusAufName] = useState(0);
  /** Wohin der Fokus nach "Notiz" oder "Wiedervorlage" im Kopf geht; die Zahl macht jeden Klick neu. */
  const [fokusAufNotiz, setFokusAufNotiz] = useState<{ readonly ziel: string; readonly mal: number }>({ ziel: NOTE_INPUT_ID, mal: 0 });
  const nameRef = useRef<HTMLDivElement | null>(null);
  /** Das gewaehlte Register: im Zustand, nicht in der Adresse; je Kontakt zurueckgesetzt (load). */
  const [register, setRegister] = useState('ueberblick');

  // Der Name liegt im Ueberblick: wer im Verlauf ist, wird dorthin gefuehrt.
  const zumAbschnitt = useCallback((abschnitt: 'name') => {
    setRegister('ueberblick');
    setOffen((jetzt) => (jetzt.includes(abschnitt) ? jetzt : [...jetzt, abschnitt]));
    setFokusAufName((zahl) => zahl + 1);
  }, []);

  // EINE Funktion fuer den Knopf "Notiz schreiben": hat das gewaehlte Register
  // keinen Verlauf, wechselt sie auf eines, das ihn hat. Der Fokus folgt, sobald
  // das Feld eingehaengt ist.
  const zumNotizkasten = useCallback((ziel: string) => {
    if (party !== undefined) {
      const angebot = angebotene(party);
      setRegister((jetzt) =>
        angebot.find((eintrag) => eintrag.id === jetzt)?.zeigtVerlauf === true
          ? jetzt
          : (angebot.find((eintrag) => eintrag.zeigtVerlauf)?.id ?? jetzt),
      );
    }
    setFokusAufNotiz((jetzt) => ({ ziel, mal: jetzt.mal + 1 }));
  }, [party]);
  const notizSchreiben = useCallback(() => zumNotizkasten(NOTE_INPUT_ID), [zumNotizkasten]);
  // "Wiedervorlage" fuehrt wie v4 in die Auswahl des Notizkastens: eine neue Notiz
  // mit Wiedervorlage. Das Ziel ist eine feste Kennung, nie ein Knopf einer
  // aelteren Notiz (Experience-Review K2, P2-1; Dirigent 12.10.2026).
  const wiedervorlageAnlegen = useCallback(() => zumNotizkasten(FOLLOW_UP_CHOICE_ID), [zumNotizkasten]);

  // Dieselbe Funktion wie der Knopf im Kopf: Taste C und Palettenpunkt fuehren
  // ebenfalls ins Notizfeld, aus jedem Register.
  useKontextbefehl(
    'notiz',
    party === undefined || status !== 'geladen'
      ? undefined
      : { label: `Notiz zu ${party.displayName}`, ausfuehren: notizSchreiben },
  );

  useEffect(() => {
    if (fokusAufNotiz.mal === 0) {
      return undefined;
    }
    let frame = 0;
    let versuche = 0;
    const suchen = (): void => {
      const feld = document.getElementById(fokusAufNotiz.ziel);
      if (feld !== null) {
        feld.scrollIntoView({ block: 'center' });
        feld.focus();
      } else if (versuche < 20) {
        versuche += 1;
        frame = requestAnimationFrame(suchen);
      }
    };
    suchen();
    return () => cancelAnimationFrame(frame);
  }, [fokusAufNotiz]);

  // Nach dem Oeffnen, nicht im Klick: vorher ist das Feld noch nicht eingehaengt.
  // Das Ziel ist der Nachname, weil er der fehlende Teil ist.
  // Das Panel haengt sich mit seiner Oeffnung ein, darum einige Frames Geduld.
  useEffect(() => {
    if (fokusAufName === 0) {
      return undefined;
    }
    let frame = 0;
    let versuche = 0;
    const suchen = (): void => {
      const feld = Array.from(nameRef.current?.querySelectorAll('input') ?? []).find((input) =>
        Array.from(input.labels ?? []).some((label) => label.textContent?.startsWith('Nachname') === true),
      );
      if (feld !== undefined) {
        feld.focus();
      } else if (versuche < 20) {
        versuche += 1;
        frame = requestAnimationFrame(suchen);
      }
    };
    suchen();
    return () => cancelAnimationFrame(frame);
  }, [fokusAufName]);

  /**
   * Der Abruf, mit einem WAECHTER gegen eine veraltete Antwort.
   *
   * Der Bildschirm bleibt beim Wechsel von `/kontakte/A` auf `/kontakte/B`
   * DIESELBE Komponente -- React tauscht nur die Eigenschaft aus. Beide Abrufe
   * koennen deshalb denselben Zustand schreiben, und wenn A nach B antwortet,
   * steht Kontakt A unter der Adresse von B. Das ist kein seltener Fall,
   * sondern der Normalfall bei einer langsamen ersten und einer schnellen
   * zweiten Anfrage.
   *
   * Die Antwort darauf ist eine LAUFNUMMER und kein Vergleich der Kennung: ein
   * Kennungsvergleich liesse zwei Antworten auf DIESELBE Kennung weiterhin um
   * den Zustand ringen -- etwa wenn jemand "Erneut versuchen" drueckt, waehrend
   * der erste Abruf noch laeuft. Nur der juengste Lauf darf schreiben.
   */
  const laufRef = useRef(0);

  const load = useCallback(
    async () => {
      const meinLauf = laufRef.current + 1;
      laufRef.current = meinLauf;
      const gueltig = (): boolean => laufRef.current === meinLauf;
      setStatus('laedt');
      setTally(undefined);
      setRegister('ueberblick');
      try {
        const token = await accessToken();
        if (!gueltig()) {
          return;
        }
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (!gueltig()) {
          return;
        }
        if (result.outcome === 'not-found') {
          setStatus('unbekannt');
          return;
        }
        if (result.outcome === 'rejected') {
          setMessage(result.message);
          setStatus('abgelehnt');
          return;
        }
        setParty(result.party);
        setStatus('geladen');
      } catch {
        if (!gueltig()) {
          return;
        }
        setMessage({
          headline: 'LIAISON ist gerade nicht erreichbar.',
          explanation: 'Der Kontakt ließ sich nicht laden.',
          nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
          retryable: true,
        });
        setStatus('abgelehnt');
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate, partyId],
  );

  useEffect(() => {
    void load();
    return () => {
      // Aufraeumen heisst hier: den laufenden Abruf entwerten. Er darf zu Ende
      // laufen -- er darf nur nichts mehr anzeigen.
      laufRef.current += 1;
    };
  }, [load]);

  // Die Sperrlage meldet das Datenpanel aus seinem Abruf; ohne sie zeigt der Kopf Text ohne Link.
  // An die Person gebunden: beim Wechsel gilt die Lage der vorigen nicht, auch nicht für einen Frame.
  const [kopfSperren, setKopfSperren] = useState<{ readonly partyId: string; readonly gesperrt: SperrenFuer | undefined }>();
  const sperrenMelden = useCallback(
    (fuer: string, gesperrt: SperrenFuer | undefined) => setKopfSperren({ partyId: fuer, gesperrt }),
    [],
  );

  const kontext: KontaktKontext | undefined =
    party === undefined
      ? undefined
      : {
          party,
          apiBaseUrl,
          accessToken,
          onReauthenticate,
          accountId,
          onParty: setParty,
          onStand: setTally,
          onSperren: sperrenMelden,
          offen,
          onOffen: setOffen,
          nameRef,
        };

  return (
    <div className={own.seite}>
      <div
        aria-live="polite"
        role="status"
        aria-label="Status: Kontakt"
        className={status === 'unbekannt' ? undefined : own.statusLeer}
      >
        {/* Das Skelett steht daneben (kein Sprung); die Ansage bleibt in der Region. */}
        {status === 'laedt' ? <Ladeansage satz="Kontakt wird geladen" /> : undefined}
        {status === 'unbekannt' ? (
          /* KEINE VERMUTUNG. Der Server antwortet auf "gibt es nicht" und
              "darfst du nicht sehen" mit demselben Ausgang, damit sich der
              Bestand nicht abzaehlen laesst. Ein Satz wie "vielleicht fehlt
              dir eine Berechtigung" wuerde genau die Auskunft geben, die der
              Server verweigert. */
          <Zustand
            art="nicht-da"
            titel="Diesen Kontakt gibt es hier nicht"
            saetze={['Er wurde entfernt, oder die Adresse ist nicht mehr gültig.', 'Geh zurück zur Liste und suche ihn dort.']}
            haupt={schnellzugriff === undefined ? { label: 'Kontakt finden', href: '/kontakte' } : { label: 'Kontakt finden', onClick: schnellzugriff }}
            neben={{ label: 'Zur Kontaktsuche', href: '/kontakte' }}
          />
        ) : undefined}
        {/* Die Region sagt den Titel an; die Seite selbst (h1, Karte, Vorgangsnummer)
            steht darunter, denn der Kopierknopf der Nummer hat eine eigene Ansage,
            und verschachtelte Live-Regionen lesen Screenreader doppelt oder gar
            nicht (Codex-Cloud-P1 #540). */}
        {status === 'abgelehnt' && message !== undefined ? message.headline : undefined}
      </div>
      {status === 'abgelehnt' && message !== undefined ? (
        <Zustand
          art="fehler"
          seite
          vorgangsnummer={message.correlationId}
          titel={message.headline}
          saetze={[message.explanation, message.nextStep]}
          haupt={
            message.retryable
              ? {
                  label: 'Erneut versuchen',
                  onClick: () => {
                    setNachErneut(true);
                    void load();
                  },
                }
              : { label: 'Zur Kontaktsuche', href: '/kontakte' }
          }
          fokus={nachErneut}
        />
      ) : undefined}

      {/* Neben der Statusregion, nicht darin: so steht das Skelett dort, wo danach
          die Kopfkarte steht, und nichts rutscht. */}
      {status === 'laedt' || (status === 'geladen' && !aktuell) ? <Skelett ladesatz="Kontakt wird geladen" kinder={<KopfSkelett />} zeilen={SKELETT_KONTAKT} /> : undefined}

      {status === 'geladen' && aktuell && party !== undefined && kontext !== undefined ? (
        <>
          {/* PARTY 360, SLICE 4b: der Kopf als Karte, darunter der Hinweis zum
              Entwurf, dann die Registerleiste. Offen bleibt im Ueberblick, was
              man zum Anrufen und Festhalten braucht -- Kontaktwege, Anschriften,
              und der Verlauf mit der Hauptaktion daneben. Zugehoerigkeit und
              Beziehungen starten OFFEN (Auflage 28.09.2026, R-B5); zugeklappt
              liegen Stichworte und Herkunft. Kein gemerkter Zustand: das
              Accordion startet immer gleich (Challenge-Review P2-2). */}
          <KontaktKopf
            party={party}
            tally={tally}
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            onParty={setParty}
            onZumAbschnitt={zumAbschnitt}
            onNotiz={notizSchreiben}
            onWiedervorlage={wiedervorlageAnlegen}
            gesperrt={kopfSperren?.partyId === party.partyId ? kopfSperren.gesperrt : undefined}
          />
          <Registerleiste key={party.partyId} kontext={kontext} gewaehlt={register} onWahl={setRegister} />
        </>
      ) : undefined}
    </div>
  );
}
